import { useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, FileDown, History, MessageSquare, Send, Undo2 } from "lucide-react";
import DashboardPage from "@/components/DashboardPage";
import { Button } from "@/components/form/Button";
import { kitButtonClass } from "../../components/kit/Button";
import { queue } from "@/components/Toast";
import { cn } from "@/utils/helpers";
import { useBrandIdentity } from "@/app/routes/dashboard/data/useCreativeOsHome";
import type { CalendarMonthPlanWithSlots, CalendarPlanSlot } from "@/types/contentCalendar.type";
import { formatLabel } from "@/types/contentCalendar.type";
import type { BriefRevisionEntry } from "@/types/productionBrief.type";
import { BRIEF_STATUS_LABEL, liveBody } from "@/types/productionBrief.type";
import {
  useAddBriefComment,
  useBriefRevisions,
  useEditBrief,
  useRecallSlotBrief,
  useSendBriefsForReview,
  useSlotBrief,
} from "../api/useProductionBrief";
import { acceptedIdea } from "../slotStage";
import { formatSlotSchedule } from "../calendar.utils";
import ProductionBriefPanel from "../components/ProductionBriefPanel";
import { ProductionBriefSkeleton } from "../components/ProductionBriefDoc";
import BriefComments from "../components/BriefComments";
import SendToClientModal from "../components/SendToClientModal";
import { exportSeatBriefPdf } from "../components/exportSeatBriefPdf";
import { usePermissions } from "@/hooks/usePermissions";
import { GUEST_STATUS_LABEL, useGuestReview } from "../guestCalendar";
import GuestBriefDecision from "../guest/GuestBriefDecision";

function WorkspaceFrame({ embedded, children }: { embedded: boolean; children: React.ReactNode }) {
  return embedded ? <>{children}</> : <DashboardPage>{children}</DashboardPage>;
}

function relativeTime(value: string | null | undefined): string {
  if (!value) return "";
  const distance = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(distance / 60_000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function TimelineDecision({ entry }: { entry: BriefRevisionEntry }) {
  if (entry.decisionsInRounds.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {entry.decisionsInRounds.map((decision, index) => {
        const approved = decision.decision === "APPROVED";
        return (
          <div
            key={`${decision.roundNumber}-${index}`}
            className={cn(
              "border-l-2 px-3 py-2.5",
              approved
                ? "border-[var(--co-live)] bg-[color-mix(in_srgb,var(--co-live)_8%,transparent)]"
                : "border-co-attention bg-[color-mix(in_srgb,var(--co-attention)_9%,transparent)]",
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <span className={cn("tt-label uppercase", approved ? "text-[var(--co-live)]" : "text-co-attention")}>
                {approved ? "Client approved" : "Client feedback"} · round {decision.roundNumber}
              </span>
              <span className="tt-label shrink-0 uppercase text-co-faint">{relativeTime(decision.decidedAt)}</span>
            </div>
            <p className="tt-read-s mt-1 text-co-text2">
              {decision.clientName}{decision.carriedOver ? " · carried forward" : ""}
            </p>
            {decision.reason ? (
              <blockquote className="tt-read-m mt-2 border-l border-co-line2 pl-3 leading-relaxed text-co-muted">
                “{decision.reason}”
              </blockquote>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function VersionTimeline({
  currentVersion,
  revisions,
}: {
  currentVersion: number;
  revisions: BriefRevisionEntry[];
}) {
  const ordered = useMemo(() => [...revisions].sort((a, b) => b.version - a.version), [revisions]);

  return (
    <section aria-label="Version history" className="min-w-0 bg-transparent">
      <header className="flex items-end justify-between gap-4 border-b border-co-line2 px-4 py-4">
        <div>
          <span className="tt-label uppercase text-co-accent">Activity</span>
          <h2 className="tt-title-m mt-1 font-extrabold uppercase text-co-text">Version history</h2>
        </div>
        <span className="tt-label uppercase text-co-faint">Newest first</span>
      </header>

      {ordered.length > 0 ? (
        <ol className="flex flex-col">
          {ordered.map((entry) => {
            const current = entry.version === currentVersion;
            return (
              <li key={entry.id} className="grid grid-cols-[3.4rem_minmax(0,1fr)] border-b border-co-line2">
                <div className="flex flex-col items-center border-r border-co-line px-2 py-5">
                  <span className={cn("font-section text-[30px] font-extrabold leading-none", current ? "text-co-accent" : "text-co-text")}>
                    v{entry.version}
                  </span>
                  <span className="tt-label mt-2 uppercase text-co-faint">{entry.authorType}</span>
                </div>
                <div className="flex min-w-0 flex-col gap-3 px-4 py-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="tt-label uppercase text-co-text2">
                      {current ? "Current version" : `Version ${entry.version}`}
                    </span>
                    <span className="tt-label uppercase text-co-faint">{relativeTime(entry.createdAt)}</span>
                  </div>
                  <p className="tt-read-s text-co-muted">
                    {entry.changeNote || (entry.version === 1 ? "Initial creative brief." : "Creative brief revised by the team.")}
                  </p>

                  {(entry.sentRounds ?? []).map((round) => (
                    <div key={round.roundNumber} className="flex items-center gap-2 border-l-2 border-co-accent pl-3">
                      <Send className="size-3 text-co-accent" />
                      <span className="tt-label uppercase text-co-faint">
                        Sent in round {round.roundNumber} · {relativeTime(round.sentAt)}
                      </span>
                    </div>
                  ))}

                  <TimelineDecision entry={entry} />

                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="tt-read-m px-4 py-6 text-co-faint">Version history appears after the first draft is saved.</p>
      )}
    </section>
  );
}

export default function PackageReviewWorkspace({
  plan,
  slot,
  canEdit,
  onBack,
  embedded = false,
  pillarHue,
  editRequest = 0,
}: {
  plan: CalendarMonthPlanWithSlots;
  slot: CalendarPlanSlot;
  canEdit: boolean;
  onBack: () => void;
  /** Render inside the Concepts home without another page shell or back control. */
  embedded?: boolean;
  /** Stable content-pillar hue shared with Calendar and Distribution. */
  pillarHue?: string;
  /** Bumped by the post page's "Edit the brief" primary. */
  editRequest?: number;
}) {
  const brand = useBrandIdentity();
  const [exporting, setExporting] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [activityPanel, setActivityPanel] = useState<"history" | "comments" | null>(null);
  const briefQuery = useSlotBrief(slot.id, true, plan.status, slot.status);
  const brief = briefQuery.data?.brief ?? null;
  const comments = briefQuery.data?.comments ?? [];
  const revisionsQuery = useBriefRevisions(brief?.id ?? null, !!brief);
  const editBrief = useEditBrief(plan.id, slot.id);
  const addComment = useAddBriefComment(slot.id);
  const sendBrief = useSendBriefsForReview(plan.id);
  const recallBrief = useRecallSlotBrief(plan.id, slot.id);
  const winner = acceptedIdea(slot);
  // A Guest (the brand's client) only ever gets a brief the team sent, and
  // answers it here as the client would.
  const { isGuest } = usePermissions();
  const guestReview = useGuestReview(plan.brandId, plan.id, slot.id);

  if (briefQuery.isLoading) {
    return (
      <WorkspaceFrame embedded={embedded}>
        <div role="status" aria-label="Loading the creative brief">
          <ProductionBriefSkeleton />
        </div>
      </WorkspaceFrame>
    );
  }

  if (!brief) {
    return (
      <WorkspaceFrame embedded={embedded}>
        {!embedded ? (
          <button type="button" onClick={onBack} className="tt-label mb-6 inline-flex items-center gap-2 uppercase text-co-muted hover:text-co-text">
            <ArrowLeft className="size-3.5" /> Back to concept review
          </button>
        ) : null}
        {briefQuery.isError ? (
          <div className="flex flex-col items-start gap-3 rounded-card bg-co-surface px-6 py-5">
            <p className="text-[15px] font-semibold text-co-text">The creative brief could not load</p>
            <p className="text-[13.5px] leading-[1.45] text-co-text2">The approved concept is safe. Try again in a moment.</p>
            <Button size="small" variant="secondary" onPress={() => void briefQuery.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          // Calendar / Brief skeleton: the brief while M7 writes it, shaped like it.
          <div role="status" aria-label="The creative brief is being written">
            <ProductionBriefSkeleton />
          </div>
        )}
      </WorkspaceFrame>
    );
  }

  const body = liveBody(brief);
  // On the team's post page the brief's comments sit in the rail's Comments
  // tab (comments/PostComments), so the island keeps only Version history.
  const commentsInRail = embedded && !isGuest;
  const hasClientReadyVersion = brief.sentVersion === null || brief.version > brief.sentVersion;
  // Embedded in the post page, the banner is the only Send and the header's
  // more menu holds Recall and Print; the standalone review keeps its own.
  const sendable =
    !embedded &&
    canEdit &&
    !!body &&
    hasClientReadyVersion &&
    ["DRAFT", "CHANGES_REQUESTED"].includes(brief.status);
  const recalling = !embedded && canEdit && brief.status === "IN_REVIEW";
  const handleExport = async () => {
    if (!body || !winner || exporting) return;
    setExporting(true);
    try {
      await exportSeatBriefPdf(
        slot,
        winner,
        {
          brandName: brand.name,
          preparedBy: brand.curatorName || undefined,
          documentVersion: brief.version,
        },
        { kind: brief.kind, status: brief.status, body },
      );
    } catch (error) {
      console.error("Production brief PDF export failed:", error);
      queue.add({
        title: "Could not export the brief",
        description: "Please try again.",
        variant: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  // The team's post page (Pen EIrjn): the date rail, the post header and its
  // status banner already carry the brief's state, version and actions, so the
  // brief stands alone under them, unboxed.
  const teamPost = embedded && !isGuest;
  const workspace = (
      <div className={cn("@container/package flex flex-col", embedded ? "gap-4" : "gap-5")}>
        {teamPost ? null : (<>
        <header className="grid gap-5 border-b border-co-line2 pb-5 @[44rem]/package:grid-cols-[minmax(0,1fr)_auto] @[44rem]/package:items-end">
          <div>
            {!embedded ? (
              <button type="button" onClick={onBack} className="tt-label mb-5 inline-flex items-center gap-2 uppercase text-co-muted hover:text-co-text">
                <ArrowLeft className="size-3.5" /> Back to client review
              </button>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <span className="tt-label uppercase text-co-accent-ink">Creative brief</span>
              <span className="inline-flex h-6 items-center rounded-tag bg-co-tonal px-2 font-mono text-[11px] uppercase leading-none tracking-[0.12em] text-co-muted">
                {isGuest
                  ? brief.status === "IN_REVIEW" ? GUEST_STATUS_LABEL.needs_approval : GUEST_STATUS_LABEL.with_team
                  : BRIEF_STATUS_LABEL[brief.status]}
              </span>
              <span className="tt-label uppercase text-co-faint">
                {isGuest
                  ? `v${brief.sentVersion ?? brief.version}`
                  : `v${brief.version}${brief.sentVersion ? ` · client saw v${brief.sentVersion}` : " · not sent"}`}
              </span>
            </div>
            {embedded ? (
              <h2 className="tt-title-l mt-2 font-extrabold uppercase text-co-text">{brief.ideaTitle || slot.title}</h2>
            ) : (
              <h1 className="tt-view-title mt-3 max-w-[20ch] text-co-text">{brief.ideaTitle || slot.title}</h1>
            )}
            <p className="tt-read-m mt-2 text-co-muted">
              {formatSlotSchedule(slot.scheduledAt)} · {formatLabel(slot)}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 @[44rem]/package:justify-end">
            {!embedded && body && winner ? (
              <Button
                variant="secondary"
                buttonStyle="outline"
                size="small"
                icon={FileDown}
                loading={exporting}
                isDisabled={exporting}
                onPress={handleExport}
              >
                Print / PDF
              </Button>
            ) : null}
            {isGuest && brief.status === "IN_REVIEW" ? (
              <GuestBriefDecision
                busy={guestReview.decideBrief.isPending}
                onApprove={() => guestReview.decideBrief.mutate({ decision: "APPROVED" })}
                onRequestChanges={(reason) => guestReview.decideBrief.mutate({ decision: "DECLINED", reason })}
              />
            ) : null}
            {sendable ? (
              <Button size="small" loading={sendBrief.isPending} onPress={() => setSendOpen(true)}>
                Send v{brief.version} to client
              </Button>
            ) : null}
            {recalling ? (
              <button
                type="button"
                disabled={recallBrief.isPending}
                onClick={() => recallBrief.mutate()}
                className={kitButtonClass({ variant: "secondary" })}
              >
                <Undo2 className="size-3.5" /> Recall creative brief
              </button>
            ) : null}
          </div>
        </header>

        <div className="relative z-20 flex justify-end">
          <section
            aria-label="Creative brief activity"
            className="relative w-full @[44rem]/package:w-[min(31rem,100%)]"
          >
          <div className={cn("grid overflow-hidden rounded-panel bg-co-surface shadow-[inset_0_0_0_1px_var(--co-line)]", commentsInRail ? "grid-cols-1" : "grid-cols-2")}>
            <button
              type="button"
              aria-expanded={activityPanel === "history"}
              aria-controls="brief-version-history"
              onClick={() => setActivityPanel((current) => current === "history" ? null : "history")}
              className={cn(
                "flex min-h-12 items-center gap-3 px-3.5 text-left transition-colors hover:bg-co-hover",
                !commentsInRail && "border-r border-co-line",
                activityPanel === "history" && "bg-co-hover",
              )}
            >
              <History className="size-4 shrink-0 text-co-accent" strokeWidth={1.8} />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-co-text">Version history</span>
                <span className="block text-[12px] text-co-faint">
                  {revisionsQuery.data?.revisions.length ?? 0} {(revisionsQuery.data?.revisions.length ?? 0) === 1 ? "version" : "versions"}
                </span>
              </span>
              <ChevronDown className={cn("size-4 shrink-0 text-co-faint transition-transform", activityPanel === "history" && "rotate-180")} strokeWidth={1.8} />
            </button>
            {commentsInRail ? null : <button
              type="button"
              aria-expanded={activityPanel === "comments"}
              aria-controls="brief-comments"
              onClick={() => setActivityPanel((current) => current === "comments" ? null : "comments")}
              className={cn(
                "flex min-h-12 items-center gap-3 px-3.5 text-left transition-colors hover:bg-co-hover",
                activityPanel === "comments" && "bg-co-hover",
              )}
            >
              <MessageSquare className="size-4 shrink-0 text-co-accent" strokeWidth={1.8} />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-co-text">Comments</span>
                <span className="block text-[12px] text-co-faint">
                  {comments.length === 0 ? "No comments" : `${comments.length} ${comments.length === 1 ? "comment" : "comments"}`}
                </span>
              </span>
              <ChevronDown className={cn("size-4 shrink-0 text-co-faint transition-transform", activityPanel === "comments" && "rotate-180")} strokeWidth={1.8} />
            </button>}
          </div>

          {activityPanel === "history" ? (
            <div id="brief-version-history" className="co-chrome-sheet custom-scrollbar absolute inset-x-0 top-full z-20 mt-2 max-h-[min(68dvh,38rem)] overflow-y-auto !rounded-panel !border-0">
              {revisionsQuery.isError ? (
                <div role="alert" className="px-5 py-5">
                  <p className="tt-read-s text-co-attention">Version history could not load. The current brief is still available below.</p>
                  <Button size="small" variant="secondary" buttonStyle="outline" className="mt-3" onPress={() => void revisionsQuery.refetch()}>Retry history</Button>
                </div>
              ) : revisionsQuery.isLoading ? (
                <p role="status" className="tt-read-s px-5 py-5 text-co-muted">Loading version history…</p>
              ) : (
                <VersionTimeline
                  currentVersion={brief.version}
                  revisions={revisionsQuery.data?.revisions ?? []}
                />
              )}
            </div>
          ) : null}

          {activityPanel === "comments" && !commentsInRail ? (
            <div id="brief-comments" className="co-chrome-sheet absolute inset-x-0 top-full z-20 mt-2 !rounded-panel !border-0">
              <header className="flex items-end justify-between gap-4 border-b border-co-line2 px-4 py-4">
                <div>
                  <span className="tt-label uppercase text-co-accent">Activity</span>
                  <h2 className="tt-title-m mt-1 font-extrabold uppercase text-co-text">Comments</h2>
                </div>
                <span className="tt-label uppercase text-co-faint">
                  {comments.length === 0 ? "No comments" : comments.length === 1 ? "1 comment" : `${comments.length} comments`}
                </span>
              </header>
              <div className="p-3">
              <BriefComments
                comments={comments}
                currentVersion={brief.version}
                onSubmit={(commentBody) =>
                  isGuest
                    ? guestReview.commentOnBrief.mutate({ briefId: brief.id, body: commentBody, briefVersion: brief.version })
                    : addComment.mutate({ briefId: brief.id, body: commentBody })
                }
                submitting={isGuest ? guestReview.commentOnBrief.isPending : addComment.isPending}
                readOnly={isGuest ? brief.status !== "IN_REVIEW" : !canEdit}
                showHeader={false}
                frameless
              />
              </div>
            </div>
          ) : null}
          </section>
        </div>
        </>)}

        <div className={cn("min-w-0", !teamPost && "overflow-hidden rounded-panel bg-co-ground")}>
          <main className={cn("min-w-0", !teamPost && "bg-co-surface p-5 @[40rem]/package:p-7 @[72rem]/package:p-8")}>
            <ProductionBriefPanel
              brief={brief}
              brandId={plan.brandId}
              canEdit={canEdit}
              planStatus={plan.status}
              winner={winner}
              pillarHue={pillarHue}
              onSave={(nextBody, changeNote, options) =>
                editBrief.mutateAsync({
                  briefId: brief.id,
                  baseVersion: options?.baseVersion ?? brief.version,
                  body: nextBody,
                  changeNote,
                  quiet: options?.quiet,
                })
              }
              saving={editBrief.isPending}
              editRequest={editRequest}
              bare={teamPost}
            />
          </main>
        </div>

        <SendToClientModal
          isOpen={sendOpen}
          planId={plan.id}
          kind="brief"
          items={[{
            slotId: slot.id,
            title: brief.ideaTitle || slot.title,
            meta: `${formatSlotSchedule(slot.scheduledAt)} · ${formatLabel(slot)}`,
            versionLabel: `v${brief.version}`,
            scheduledAt: slot.scheduledAt,
          }]}
          busy={sendBrief.isPending}
          onPublish={(slotIds) => sendBrief.mutateAsync(slotIds).then(() => undefined)}
          onClose={() => setSendOpen(false)}
        />
      </div>
  );

  return <WorkspaceFrame embedded={embedded}>{workspace}</WorkspaceFrame>;
}
